// 审计页：Phase 1 完整版。
// - 过滤器（domain / actor / action / subject_id）+ limit 切换。
// - 过滤器状态通过 TanStack Router search params 持久化（URL 可分享/可刷新）。
// - QueryView 统一处理三态。filter / limit 变化 → useQuery key 变化自动重取。
//
// 视图层（AuditView）跟路由解耦，方便 vitest 直接 mount 不需要 RouterProvider。

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, type FormEvent } from "react";
import { apiGet } from "@/api/client";
import { API_AUDIT } from "@/api/endpoints";
import type { AuditListResponse } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { formatMs } from "@/lib/format";

// 允许的 limit 档位；超出 daemon 上限 500 的不放进来。
export const AUDIT_LIMIT_OPTIONS = [50, 100, 200, 500] as const;
const DEFAULT_LIMIT = 100;

// search params schema：手写解析，不引入 zod。
// daemon 端 subject_id 是 int64，前端用 number 即可（JS 安全整数足够日常 ID）。
export interface AuditSearch {
  domain?: string;
  actor?: string;
  action?: string;
  subject_id?: number;
  // limit 可选：URL 不带就用 DEFAULT_LIMIT。声明可选才能让 <Link to="/audit" search={{}}> 不报必填。
  limit?: number;
}

function parseAuditSearch(search: Record<string, unknown>): AuditSearch {
  // limit：白名单内取值，否则回 undefined（视图层用 DEFAULT_LIMIT 兜底）。
  const rawLimit = Number(search.limit);
  const limit = (AUDIT_LIMIT_OPTIONS as readonly number[]).includes(rawLimit)
    ? rawLimit
    : undefined;

  const pickStr = (v: unknown): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t === "" ? undefined : t;
  };
  const pickInt = (v: unknown): number | undefined => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : undefined;
  };
  return {
    domain: pickStr(search.domain),
    actor: pickStr(search.actor),
    action: pickStr(search.action),
    subject_id: pickInt(search.subject_id),
    limit,
  };
}

export const Route = createFileRoute("/audit")({
  validateSearch: parseAuditSearch,
  component: AuditRouteComponent,
});

function AuditRouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/audit" });
  return (
    <AuditView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

// 把 search 组装成 query string（只塞非空字段）。
export function buildAuditQuery(search: AuditSearch): string {
  const params = new URLSearchParams();
  if (search.domain) params.set("domain", search.domain);
  if (search.actor) params.set("actor", search.actor);
  if (search.action) params.set("action", search.action);
  if (typeof search.subject_id === "number") {
    params.set("subject_id", String(search.subject_id));
  }
  params.set("limit", String(search.limit ?? DEFAULT_LIMIT));
  return params.toString();
}

interface AuditViewProps {
  search: AuditSearch;
  onChange: (next: AuditSearch) => void;
}

// 与路由解耦的纯视图组件；test 里直接 mount 它。
export function AuditView({ search, onChange }: AuditViewProps) {
  // 本地表单状态（受控 input），点"应用"才同步进 URL search。
  // limit 在 form 里始终具体化，避免 select 受控警告。
  const effectiveLimit = search.limit ?? DEFAULT_LIMIT;
  const [form, setForm] = useState<AuditSearch>({ ...search, limit: effectiveLimit });
  useEffect(() => {
    setForm({ ...search, limit: search.limit ?? DEFAULT_LIMIT });
  }, [search]);

  const q = useQuery({
    queryKey: ["audit", search],
    queryFn: () => apiGet<AuditListResponse>(`${API_AUDIT}?${buildAuditQuery(search)}`),
  });

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onChange(form);
  }

  function reset() {
    const next: AuditSearch = { limit: DEFAULT_LIMIT };
    setForm(next);
    onChange(next);
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={apply}
        aria-label="审计过滤器"
        className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 sm:grid-cols-5"
      >
        <FilterInput
          label="domain"
          value={form.domain ?? ""}
          onChange={(v) => setForm({ ...form, domain: v || undefined })}
        />
        <FilterInput
          label="actor"
          value={form.actor ?? ""}
          onChange={(v) => setForm({ ...form, actor: v || undefined })}
        />
        <FilterInput
          label="action"
          value={form.action ?? ""}
          onChange={(v) => setForm({ ...form, action: v || undefined })}
        />
        <FilterInput
          label="subject_id"
          value={
            typeof form.subject_id === "number" ? String(form.subject_id) : ""
          }
          onChange={(v) => {
            const trimmed = v.trim();
            if (trimmed === "") {
              setForm({ ...form, subject_id: undefined });
              return;
            }
            const n = Number(trimmed);
            setForm({
              ...form,
              subject_id: Number.isFinite(n) && n >= 0 ? Math.trunc(n) : undefined,
            });
          }}
          inputMode="numeric"
        />
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            limit
          </span>
          <select
            value={form.limit ?? DEFAULT_LIMIT}
            onChange={(e) =>
              setForm({ ...form, limit: Number(e.target.value) || DEFAULT_LIMIT })
            }
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            {AUDIT_LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2 sm:col-span-5">
          <Button type="submit" size="sm">
            应用
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset}>
            重置
          </Button>
          <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">
            {q.data ? `共 ${q.data.count} 条` : ""}
          </span>
        </div>
      </form>

      <QueryView query={q} isEmpty={(d) => d.items.length === 0}>
        {(data) => (
          <table className="w-full border-collapse text-sm">
            <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
              <tr>
                <th className="py-2 pr-3">时间</th>
                <th className="py-2 pr-3">域</th>
                <th className="py-2 pr-3">动作</th>
                <th className="py-2 pr-3">操作者</th>
                <th className="py-2 pr-3">对象 ID</th>
                <th className="py-2 pr-3">负载</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-[hsl(var(--border))]/60 align-top"
                >
                  <td className="py-1.5 pr-3 font-mono text-xs">
                    {formatMs(row.created_at)}
                  </td>
                  <td className="py-1.5 pr-3">{row.domain}</td>
                  <td className="py-1.5 pr-3">{row.action}</td>
                  <td className="py-1.5 pr-3">{row.actor}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{row.subject_id}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-[hsl(var(--muted-foreground))]">
                    {row.payload_json.length > 120
                      ? `${row.payload_json.slice(0, 120)}…`
                      : row.payload_json}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </QueryView>
    </div>
  );
}

interface FilterInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputMode?: "text" | "numeric";
}

function FilterInput({ label, value, onChange, inputMode }: FilterInputProps) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <input
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode={inputMode}
        className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
      />
    </label>
  );
}
